import { inject, Injectable } from '@angular/core';
import { AuthService } from '@nimmsta/ng-commons-lib/auth';
import { LoadType, Pageable, UserRole } from '@nimmsta/ng-commons-lib/models';
import { WorkflowDataService } from '@nimmsta/ng-commons-lib/data-access';
import { concatMap, map, Observable, of, take, tap } from 'rxjs';
import { Mdm } from '../../models/mdm';

@Injectable({ providedIn: 'root' })
export class WorkerNameService extends WorkflowDataService<Mdm> {
  private handheldIdToWorkerName: { [key: string]: string } = {};

  private authService = inject(AuthService);

  protected override get apiEndpoint(): string {
    return '/handheld';
  }

  getNameMap(ids: string[]): Observable<{ [key: string]: string }> {
    const handheldIds = ids.filter((id) => !this.handheldIdToWorkerName[id]).filter(this.unique);
    if (handheldIds.length === 0) {
      return of(this.generateMap(ids));
    }
    return this.authService.getCurrentUser()
      .pipe(
        take(1),
        map((user) => (user.role === UserRole.ADMIN ? LoadType.ALL : LoadType.ORGANIZATION)),
        concatMap((loadType) => this.loadHandheldsAndSaveNames(loadType, handheldIds)),
        map(() => this.generateMap(ids)),
      );
  }

  private loadHandheldsAndSaveNames(loadType: LoadType, ids: string[]): Observable<Pageable<Mdm>> {
    return this.http.get<Pageable<Mdm>>(this.baseUrl + this.apiEndpoint, {
      params: { unpaged: true, loadType, filters: this.buildIdFilter(ids) },
    }).pipe(
      tap((page) => {
        page.content.forEach((handheld) => {
          this.handheldIdToWorkerName[handheld.id] = handheld.occupyingWorker
            ? `${handheld.occupyingWorker.given_name} ${handheld.occupyingWorker.family_name}`.trim() : '';

          // fix for NIMRND-8322
          this.handheldIdToWorkerName[handheld.deviceId] = handheld.occupyingWorker
            ? `${handheld.occupyingWorker.given_name} ${handheld.occupyingWorker.family_name}`.trim() : '';
        });
      }),
    );
  }

  private buildIdFilter(ids: string[]): string[] {
    // deviceId needed for NIMRND-8322
    return ids.map((id) => [`id:${id}-or`, `deviceId:${id}-or`]).flat();
  }

  private unique(value: string, index: number, self: string[]): boolean {
    return self.indexOf(value) === index;
  }

  private generateMap(ids: string[]): { [key: string]: string } {
    const workerMap: { [key: string]: string } = {};
    ids.forEach((id) => {
      workerMap[id] = this.handheldIdToWorkerName[id] ?? '';
    });
    return workerMap;
  }
}
